/**
 * useThreads hook - manages thread state and operations
 * Handles thread list, selection, messages, typing indicators, etc.
 */
'use client';

import { useState, useCallback, useRef, useMemo, useEffect, type SetStateAction } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import messagesStore from '@/lib/offline/messagesStore';
import type {
  ClientThread,
  ThreadMessage,
  EligibleMember,
  TypingUser,
  PinnedMention
} from '../types';

interface UseThreadsOptions {
  crossClientMode?: boolean;
}

function getThreadIdentity(thread: Pick<ClientThread, 'thread_id' | 'id'> | null | undefined): string | null {
  const identity = thread?.thread_id || thread?.id;
  return identity !== undefined && identity !== null ? String(identity) : null;
}

function getThreadSortTimestamp(thread: ClientThread): number {
  const sourceDate = thread.last_message_at || thread.updated_at || thread.created_at;
  const timestamp = sourceDate ? new Date(sourceDate).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function getThreadSearchText(thread: ClientThread): string {
  const memberText = (thread.members || [])
    .flatMap((member) => [member.display_name, member.email])
    .filter(Boolean)
    .join(' ');

  return [
    thread.thread_name,
    thread.title,
    thread.thread_description,
    thread.description,
    thread.last_message_preview,
    thread.last_message_sender,
    thread.client_name,
    thread.job_number,
    thread.team_name,
    thread.contact_name,
    thread.contact_location,
    memberText,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getPortalClientIdFallback(): number {
  if (typeof window === 'undefined') {
    return 0;
  }
  const raw = window.localStorage.getItem('current_client_id');
  const parsed = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function useThreads(options: UseThreadsOptions = {}) {
  const { user, client } = useAuth();
  const getCacheClientId = useCallback(() => {
    const authClientId = Number(client?.id || 0);
    return Number.isFinite(authClientId) && authClientId > 0 ? authClientId : getPortalClientIdFallback();
  }, [client?.id]);

  // Thread state
  const [threads, setThreads] = useState<ClientThread[]>([]);
  const [selectedThread, setSelectedThreadState] = useState<ClientThread | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [eligibleMembers, setEligibleMembers] = useState<EligibleMember[]>([]);
  const [loading, setLoading] = useState(true);

  // Cross-client mode state
  const [isCrossClientMode, setIsCrossClientMode] = useState(options.crossClientMode ?? true);
  const [totalClientsInThreads, setTotalClientsInThreads] = useState(1);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ClientThread[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'name' | 'unread'>('newest');

  // Typing indicators
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingUser[]>>(new Map());
  const typingTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Pinned mentions
  const [pinnedMentions, setPinnedMentions] = useState<PinnedMention[]>([]);

  // Refs for accessing current state in callbacks
  const selectedThreadRef = useRef<ClientThread | null>(null);
  const setSelectedThread = useCallback((nextThread: SetStateAction<ClientThread | null>) => {
    const resolvedThread = typeof nextThread === 'function'
      ? (nextThread as (prevState: ClientThread | null) => ClientThread | null)(selectedThreadRef.current)
      : nextThread;

    selectedThreadRef.current = resolvedThread;
    setSelectedThreadState(resolvedThread);
  }, []);

  useEffect(() => {
    selectedThreadRef.current = selectedThread;
  }, [selectedThread]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;

    const hydrateCachedThreads = async () => {
      const cacheClientId = getCacheClientId();
      if (!cacheClientId) {
        if (!cancelled) {
          setLoading(false);
        }
        return;
      }

      try {
        const cachedThreads = await messagesStore.listCachedThreads<ClientThread>(cacheClientId);
        if (!cancelled) {
          if (cachedThreads.length > 0) {
            setThreads(cachedThreads);
          }
          setLoading(false);
        }
      } catch (error) {
        console.warn('[useThreads] Failed to hydrate cached threads:', error);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void hydrateCachedThreads();

    return () => {
      cancelled = true;
    };
  }, [getCacheClientId]);

  // Get sorted and filtered threads
  const sortedThreads = useMemo(() => {
    const threadsToDisplay = searchResults ?? threads;

    // Filter by search query (client-side)
    let filtered = threadsToDisplay;
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (normalizedQuery && !searchResults) {
      filtered = threadsToDisplay.filter((thread) => getThreadSearchText(thread).includes(normalizedQuery));
    }

    // Sort
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return getThreadSortTimestamp(b) - getThreadSortTimestamp(a);
        case 'oldest':
          return getThreadSortTimestamp(a) - getThreadSortTimestamp(b);
        case 'name':
          return (a.thread_name || a.title || '').localeCompare(b.thread_name || b.title || '', undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        case 'unread': {
          const unreadBucketDiff = Number((b.unread_count || 0) > 0) - Number((a.unread_count || 0) > 0);
          if (unreadBucketDiff !== 0) {
            return unreadBucketDiff;
          }

          const unreadCountDiff = (b.unread_count || 0) - (a.unread_count || 0);
          if (unreadCountDiff !== 0) {
            return unreadCountDiff;
          }

          return getThreadSortTimestamp(b) - getThreadSortTimestamp(a);
        }
        default:
          return 0;
      }
    });
  }, [threads, searchResults, searchQuery, sortBy]);

  // Calculate total unread count
  const totalUnreadCount = useMemo(() => {
    return threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);
  }, [threads]);

  // Get typing users for current thread
  const currentTypingUsers = useMemo(() => {
    const selectedThreadId = getThreadIdentity(selectedThread);
    if (!selectedThreadId) return [];
    return typingUsers.get(selectedThreadId) || [];
  }, [selectedThread, typingUsers]);

  // --- Thread list handlers ---

  const handleThreadsList = useCallback((
    newThreads: ClientThread[],
    crossClient: boolean,
    totalClients: number
  ) => {
    console.log('[useThreads] handleThreadsList called with', newThreads?.length || 0, 'threads, crossClient:', crossClient);
    setThreads(newThreads);
    setIsCrossClientMode(crossClient);
    setTotalClientsInThreads(totalClients);
    setLoading(false);

    const cacheClientId = getCacheClientId();
    if (cacheClientId) {
      void messagesStore.storeThreads(cacheClientId, newThreads as unknown as Record<string, unknown>[])
        .catch((error) => console.warn('[useThreads] Failed to cache thread list:', error));
    }
  }, [getCacheClientId]);

  const handleThreadCreated = useCallback((thread: ClientThread) => {
    setThreads(prev => [thread, ...prev]);
    setSelectedThread(thread);
  }, [setSelectedThread]);

  const handleThreadUpdated = useCallback((thread: ClientThread) => {
    const updatedThreadId = getThreadIdentity(thread);

    setThreads(prev => prev.map(t =>
      getThreadIdentity(t) === updatedThreadId ? thread : t
    ));
    if (getThreadIdentity(selectedThreadRef.current) === updatedThreadId) {
      setSelectedThread(thread);
    }
  }, [setSelectedThread]);

  const handleThreadDeleted = useCallback((threadId: string) => {
    const normalizedThreadId = String(threadId);

    setThreads(prev => prev.filter(t => getThreadIdentity(t) !== normalizedThreadId));
    if (getThreadIdentity(selectedThreadRef.current) === normalizedThreadId) {
      setSelectedThread(null);
      setMessages([]);
    }
  }, [setSelectedThread]);

  const handleSearchResults = useCallback((results: ClientThread[], query: string) => {
    setSearchResults(query.trim() ? results : null);
    setIsSearching(false);
  }, []);

  // --- Message handlers ---

  const handleThreadMessages = useCallback(async (threadId: string, newMessages: ThreadMessage[]) => {
    const currentThreadId = getThreadIdentity(selectedThreadRef.current);
    const normalizedThreadId = String(threadId);

    // Only update if messages are for current thread
    if (currentThreadId !== normalizedThreadId) {
      return;
    }

    setMessages(newMessages);

    const cacheClientId = getCacheClientId();
    if (cacheClientId) {
      void messagesStore.upsertThreadMessages(
        cacheClientId,
        normalizedThreadId,
        newMessages as unknown as Record<string, unknown>[],
      ).catch((error) => console.warn('[useThreads] Failed to cache thread messages:', error));
    }

    // Extract pinned mentions for current user
    if (user?.id && newMessages) {
      const mentionsForUser = newMessages.filter(msg =>
        msg.mentions?.includes(user.id) && msg.sender_user_id !== user.id
      ).map(msg => ({
        message_id: msg._id,
        content: msg.content,
        message_preview: msg.content?.substring(0, 100) + (msg.content?.length > 100 ? '...' : ''),
        sender_name: msg.sender_name,
        sender_avatar: msg.sender_avatar,
        timestamp: msg.timestamp,
        dismissed: false
      }));

      // Get dismissed mentions from localStorage (immediate)
      const dismissedKey = `dismissed_mentions_${normalizedThreadId}`;
      let dismissedIds: string[] = JSON.parse(localStorage.getItem(dismissedKey) || '[]');

      // Also fetch from server (for cross-platform sync) and merge
      try {
        const tokens = authService.getTokens();
        if (tokens?.access_token) {
          const response = await fetch(`/api/v1/client-threads/dismiss-mention?thread_id=${threadId}`, {
            headers: { 'Authorization': `Bearer ${tokens.access_token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.dismissed_message_ids)) {
              // Merge server IDs with local IDs
              const serverIds = data.dismissed_message_ids;
              dismissedIds = [...new Set([...dismissedIds, ...serverIds])];
              // Update localStorage with merged set
              localStorage.setItem(dismissedKey, JSON.stringify(dismissedIds));
            }
          }
        }
      } catch (err) {
        // Silently fail - localStorage is the fallback
      }

      const filteredMentions = mentionsForUser.filter(m => !dismissedIds.includes(m.message_id));
      setPinnedMentions(filteredMentions);
    }
  }, [getCacheClientId, user?.id]);

  const handleNewMessage = useCallback((threadId: string, message: ThreadMessage) => {
    const normalizedThreadId = String(threadId);

    // Add to messages if viewing this thread
    if (getThreadIdentity(selectedThreadRef.current) === normalizedThreadId) {
      setMessages(prev => {
        // Helper to check if attachments match (by URL since they're the same files)
        const attachmentsMatch = (a: ThreadMessage['attachments'], b: ThreadMessage['attachments']) => {
          if (!a?.length && !b?.length) return false; // Both empty - can't use this for matching
          if (a?.length !== b?.length) return false;
          const aUrls = new Set(a?.map(att => att.url));
          return b?.every(att => aUrls.has(att.url));
        };

        // Helper to safely compare user IDs (handles string/number mismatch)
        const sameUser = (a: unknown, b: unknown) => String(a) === String(b);

        // Helper to check if timestamps are within 30 seconds (for optimistic matching)
        const closeInTime = (a: string | undefined, b: string | undefined) => {
          if (!a || !b) return false;
          const aTime = new Date(a).getTime();
          const bTime = new Date(b).getTime();
          return Math.abs(aTime - bTime) < 30000; // 30 seconds
        };

        // Check for duplicates - also handle case where this is confirming an optimistic message
        const existingIndex = prev.findIndex(m =>
          m._id === message._id ||
          m.id === message.id ||
          // Match pending message by same user + (same content OR same attachments) + close timestamp
          (m.pending && sameUser(m.sender_user_id, message.sender_user_id) && (
            (m.content === message.content && closeInTime(m.timestamp || m.created_at, message.timestamp || message.created_at)) ||
            attachmentsMatch(m.attachments, message.attachments)
          ))
        );

        if (existingIndex >= 0) {
          // If it was a pending message, replace it with the confirmed one
          if (prev[existingIndex].pending) {
            const updated = [...prev];
            updated[existingIndex] = { ...message, pending: false };
            const cacheClientId = getCacheClientId();
            if (cacheClientId) {
              void messagesStore.upsertThreadMessages(
                cacheClientId,
                normalizedThreadId,
                updated as unknown as Record<string, unknown>[],
              ).catch(() => undefined);
            }
            return updated;
          }
          return prev; // Already exists, don't add duplicate
        }
        const next = [...prev, message];
        const cacheClientId = getCacheClientId();
        if (cacheClientId) {
          void messagesStore.upsertThreadMessages(
            cacheClientId,
            normalizedThreadId,
            next as unknown as Record<string, unknown>[],
          ).catch(() => undefined);
        }
        return next;
      });

      // Check if current user is mentioned in this new message
      if (user?.id && message.mentions?.includes(user.id) && message.sender_user_id !== user.id) {
        const dismissedKey = `dismissed_mentions_${normalizedThreadId}`;
        const dismissedIds = JSON.parse(localStorage.getItem(dismissedKey) || '[]');

        // Only add if not already dismissed
        if (!dismissedIds.includes(message._id)) {
          const newMention: PinnedMention = {
            message_id: message._id,
            content: message.content,
            message_preview: message.content?.substring(0, 100) + (message.content?.length > 100 ? '...' : ''),
            sender_name: message.sender_name,
            sender_avatar: message.sender_avatar,
            timestamp: message.timestamp,
            dismissed: false
          };

          setPinnedMentions(prev => {
            // Avoid duplicates
            if (prev.some(m => m.message_id === message._id)) return prev;
            return [...prev, newMention];
          });
        }
      }
    }

    // Update thread list preview
    setThreads(prev => prev.map(t =>
      getThreadIdentity(t) === normalizedThreadId
        ? {
          ...t,
          last_message_at: message.timestamp,
          last_message_preview: message.content,
          last_message_sender: message.sender_name,
          // Don't update unread here - badge_counts_updated handles it
        }
        : t
    ));
  }, [getCacheClientId, user?.id]);

  const handleMessageSent = useCallback((threadId: string, message: ThreadMessage) => {
    const normalizedThreadId = String(threadId);

    // Add to thread messages if viewing this thread
    if (getThreadIdentity(selectedThreadRef.current) === normalizedThreadId) {
      setMessages(prev => {
        // Helper to check if attachments match (by URL since they're the same files)
        const attachmentsMatch = (a: ThreadMessage['attachments'], b: ThreadMessage['attachments']) => {
          if (!a?.length && !b?.length) return false; // Both empty - can't use this for matching
          if (a?.length !== b?.length) return false;
          // Compare by URL which should be the same for the same attachment
          const aUrls = new Set(a?.map(att => att.url));
          return b?.every(att => aUrls.has(att.url));
        };

        // Helper to safely compare user IDs (handles string/number mismatch)
        const sameUser = (a: unknown, b: unknown) => String(a) === String(b);

        // Helper to check if timestamps are within 30 seconds (for optimistic matching)
        const closeInTime = (a: string | undefined, b: string | undefined) => {
          if (!a || !b) return false;
          const aTime = new Date(a).getTime();
          const bTime = new Date(b).getTime();
          return Math.abs(aTime - bTime) < 30000; // 30 seconds
        };

        // Check for duplicates by _id, id, or if this is confirming an optimistic message
        // Also check temp_id from server response matching our pending message
        const isDuplicate = prev.some(m =>
          m._id === message._id ||
          m.id === message.id ||
          (m.pending && m.temp_id && (message as { temp_id?: string }).temp_id === m.temp_id) ||
          (m.pending && sameUser(m.sender_user_id, message.sender_user_id) && (
            (m.content === message.content && closeInTime(m.timestamp || m.created_at, message.timestamp || message.created_at)) ||
            // For attachment messages, content might differ (empty vs '📎')
            attachmentsMatch(m.attachments, message.attachments)
          ))
        );

        if (isDuplicate) {
          // Replace the optimistic/pending message with the confirmed one
          const next = prev.map(m => {
            if (m.pending && sameUser(m.sender_user_id, message.sender_user_id) && (
              (m.temp_id && (message as { temp_id?: string }).temp_id === m.temp_id) ||
              (m.content === message.content && closeInTime(m.timestamp || m.created_at, message.timestamp || message.created_at)) ||
              attachmentsMatch(m.attachments, message.attachments)
            )) {
              return { ...message, pending: false };
            }
            return m;
          });
          const cacheClientId = getCacheClientId();
          if (cacheClientId) {
            void messagesStore.upsertThreadMessages(
              cacheClientId,
              normalizedThreadId,
              next as unknown as Record<string, unknown>[],
            ).catch(() => undefined);
          }
          return next;
        }
        const next = [...prev, message];
        const cacheClientId = getCacheClientId();
        if (cacheClientId) {
          void messagesStore.upsertThreadMessages(
            cacheClientId,
            normalizedThreadId,
            next as unknown as Record<string, unknown>[],
          ).catch(() => undefined);
        }
        return next;
      });
    }

    // Update thread list preview
    setThreads(prev => prev.map(t =>
      getThreadIdentity(t) === normalizedThreadId
        ? {
          ...t,
          last_message_at: message.timestamp,
          last_message_preview: message.content,
          last_message_sender: message.sender_name
        }
        : t
    ));
  }, [getCacheClientId]);

  const handleReactionUpdated = useCallback((
    threadId: string,
    messageId: string,
    reactions: NonNullable<ThreadMessage['reactions']>
  ) => {
    if (getThreadIdentity(selectedThreadRef.current) === String(threadId)) {
      const normalizedMessageId = String(messageId);
      setMessages(prev => prev.map(msg => {
        const candidateIds = [
          msg._id,
          msg.id,
          msg.temp_id,
          (msg as ThreadMessage & { message_id?: string }).message_id,
        ].filter(Boolean).map((value) => String(value));

        return candidateIds.includes(normalizedMessageId)
          ? { ...msg, reactions }
          : msg;
      }));
    }
  }, []);

  const handleReadReceipt = useCallback((
    threadId: string,
    userId: number,
    userName: string,
    readAt: string
  ) => {
    if (getThreadIdentity(selectedThreadRef.current) === String(threadId)) {
      setMessages(prev => prev.map(msg => {
        // Only update messages sent by current user
        if (msg.sender_user_id === user?.id) {
          const alreadyRead = msg.read_by?.some(r => r.user_id === userId);
          if (!alreadyRead) {
            return {
              ...msg,
              read_by: [...(msg.read_by || []), { user_id: userId, read_at: readAt, user_name: userName }]
            };
          }
        }
        return msg;
      }));
    }
  }, [user?.id]);

  // --- Badge handlers ---

  const handleBadgeCountsUpdated = useCallback((
    threadId: string,
    unreadCount: number,
    lastMessage?: ThreadMessage | null
  ) => {
    const currentThreadId = getThreadIdentity(selectedThreadRef.current);
    const normalizedThreadId = String(threadId);

    setThreads(prev => prev.map(t =>
      getThreadIdentity(t) === normalizedThreadId
        ? {
          ...t,
          ...(lastMessage
            ? {
              last_message_at: lastMessage.timestamp || lastMessage.created_at || t.last_message_at,
              last_message_preview: typeof lastMessage.content === 'string'
                ? lastMessage.content
                : String(lastMessage.content || t.last_message_preview || ''),
              last_message_sender: lastMessage.sender_name || t.last_message_sender,
            }
            : {}),
          // If viewing this thread, keep at 0; otherwise use server count
          unread_count: getThreadIdentity(t) === currentThreadId ? 0 : unreadCount
        }
        : t
    ));
  }, []);

  // --- Member handlers ---

  const handleEligibleMembers = useCallback((members: EligibleMember[]) => {
    setEligibleMembers(members);
  }, []);

  const handleMemberAdded = useCallback((threadId: string, member: ClientThread['members'][number]) => {
    if (getThreadIdentity(selectedThreadRef.current) === String(threadId)) {
      setSelectedThread(prev => prev ? {
        ...prev,
        members: [...prev.members, member],
        member_count: prev.member_count + 1
      } : null);
    }
  }, [setSelectedThread]);

  const handleMemberRemoved = useCallback((threadId: string, userId: number) => {
    if (getThreadIdentity(selectedThreadRef.current) === String(threadId)) {
      setSelectedThread(prev => prev ? {
        ...prev,
        members: prev.members.filter(m => m.user_id !== userId),
        member_count: prev.member_count - 1
      } : null);
    }
  }, [setSelectedThread]);

  const handleRemovedFromThread = useCallback((threadId: string) => {
    const normalizedThreadId = String(threadId);

    setThreads(prev => prev.filter(t => getThreadIdentity(t) !== normalizedThreadId));
    if (getThreadIdentity(selectedThreadRef.current) === normalizedThreadId) {
      setSelectedThread(null);
      setMessages([]);
    }
  }, [setSelectedThread]);

  // --- Typing handlers ---

  const handleUserTyping = useCallback((threadId: string, typingUser: TypingUser) => {
    if (typingUser.userId === user?.id) return;

    setTypingUsers(prev => {
      const updated = new Map(prev);
      const currentUsers = updated.get(threadId) || [];

      // Remove existing entry for this user and add new one
      const filtered = currentUsers.filter(u => u.userId !== typingUser.userId);
      filtered.push(typingUser);

      updated.set(threadId, filtered);
      return updated;
    });

    // Auto-clear typing after 5 seconds
    const timeoutKey = `${threadId}_${typingUser.userId}`;
    const existingTimeout = typingTimeoutRef.current.get(timeoutKey);
    if (existingTimeout) clearTimeout(existingTimeout);

    typingTimeoutRef.current.set(timeoutKey, setTimeout(() => {
      setTypingUsers(prev => {
        const updated = new Map(prev);
        const currentUsers = updated.get(threadId) || [];
        updated.set(threadId, currentUsers.filter(u => u.userId !== typingUser.userId));
        return updated;
      });
      typingTimeoutRef.current.delete(timeoutKey);
    }, 5000));
  }, [user?.id]);

  const handleUserStoppedTyping = useCallback((threadId: string, userId: number) => {
    if (userId === user?.id) return;

    setTypingUsers(prev => {
      const updated = new Map(prev);
      const currentUsers = updated.get(threadId) || [];
      updated.set(threadId, currentUsers.filter(u => u.userId !== userId));
      return updated;
    });

    // Clear timeout
    const timeoutKey = `${threadId}_${userId}`;
    const existingTimeout = typingTimeoutRef.current.get(timeoutKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      typingTimeoutRef.current.delete(timeoutKey);
    }
  }, [user?.id]);

  // --- Pinned mentions ---

  const dismissMention = useCallback(async (messageId: string) => {
    if (!selectedThread) return;

    const threadId = selectedThread.thread_id || selectedThread.id;

    // Update local storage for immediate UI response
    const dismissedKey = `dismissed_mentions_${threadId}`;
    const dismissedIds = JSON.parse(localStorage.getItem(dismissedKey) || '[]');
    dismissedIds.push(messageId);
    localStorage.setItem(dismissedKey, JSON.stringify(dismissedIds));

    // Update UI immediately
    setPinnedMentions(prev => prev.filter(m => m.message_id !== messageId));

    // Sync to server (so it persists across platforms)
    try {
      const tokens = authService.getTokens();
      if (tokens?.access_token) {
        await fetch('/api/v1/client-threads/dismiss-mention', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            thread_id: threadId,
            message_id: messageId
          })
        });
      }
    } catch (err) {
      console.error('[useThreads] Error syncing mention dismissal:', err);
      // Continue - local storage is the fallback
    }
  }, [selectedThread]);

  const dismissAllMentions = useCallback(async () => {
    if (!selectedThread) return;

    const threadId = selectedThread.thread_id || selectedThread.id;

    // Get all mention IDs before clearing
    const messageIds = pinnedMentions.map(m => m.message_id);

    // Update local storage for immediate UI response
    const dismissedKey = `dismissed_mentions_${threadId}`;
    const dismissedIds = JSON.parse(localStorage.getItem(dismissedKey) || '[]');

    // Add all current pinned mentions to dismissed list
    messageIds.forEach(id => {
      if (!dismissedIds.includes(id)) {
        dismissedIds.push(id);
      }
    });

    localStorage.setItem(dismissedKey, JSON.stringify(dismissedIds));

    // Update UI immediately
    setPinnedMentions([]);

    // Sync to server (so it persists across platforms)
    try {
      const tokens = authService.getTokens();
      if (tokens?.access_token) {
        await fetch('/api/v1/client-threads/dismiss-mention', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            thread_id: threadId,
            message_ids: messageIds // Bulk dismiss
          })
        });
      }
    } catch (err) {
      console.error('[useThreads] Error syncing mention dismissals:', err);
      // Continue - local storage is the fallback
    }
  }, [selectedThread, pinnedMentions]);

  // --- Thread selection ---

  const selectThread = useCallback((thread: ClientThread | null) => {
    setSelectedThread(thread);
    if (!thread) {
      setMessages([]);
      setPinnedMentions([]);
      return;
    }

    const threadId = getThreadIdentity(thread);
    const cacheClientId = getCacheClientId();
    setMessages([]);

    if (threadId && cacheClientId) {
      void messagesStore.getCachedThreadMessages<ThreadMessage>(cacheClientId, threadId)
        .then((cachedMessages) => {
          if (
            cachedMessages.length > 0 &&
            getThreadIdentity(selectedThreadRef.current) === threadId
          ) {
            setMessages(cachedMessages);
          }
        })
        .catch((error) => console.warn('[useThreads] Failed to load cached thread messages:', error));
    }
  }, [getCacheClientId, setSelectedThread]);

  // --- HTTP fallback methods ---

  const fetchThreadsHTTP = useCallback(async () => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/client-threads', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch client threads (${response.status})`);
      }

      const data = await response.json();
      const fetchedThreads = data.threads || [];
      setThreads(fetchedThreads);
      setIsCrossClientMode(data.cross_client ?? true);
      setTotalClientsInThreads(data.total_clients ?? 1);
      setLoading(false);

      const cacheClientId = getCacheClientId();
      if (cacheClientId) {
        void messagesStore.storeThreads(cacheClientId, fetchedThreads as unknown as Record<string, unknown>[])
          .catch((error) => console.warn('[useThreads] Failed to cache HTTP thread list:', error));
      }
    } catch (error) {
      console.error('[useThreads] HTTP fetch failed:', error);
      setLoading(false);
    }
  }, [getCacheClientId]);

  const fetchMessagesHTTP = useCallback(async (threadId: string) => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch(`/api/v1/messaging/threads/${encodeURIComponent(threadId)}/messages`, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch thread messages (${response.status})`);
      }

      const data = await response.json();
      const fetchedMessages = data.messages || [];
      setMessages(fetchedMessages);

      const cacheClientId = getCacheClientId();
      if (cacheClientId) {
        void messagesStore.upsertThreadMessages(
          cacheClientId,
          String(threadId),
          fetchedMessages as unknown as Record<string, unknown>[],
        ).catch((error) => console.warn('[useThreads] Failed to cache HTTP thread messages:', error));
      }
    } catch (error) {
      console.error('[useThreads] HTTP message fetch failed:', error);
    }
  }, [getCacheClientId]);

  // Add optimistic message (for offline queue)
  const handleMessageDeleted = useCallback((threadId: string, messageId: string) => {
    setMessages(prev => prev.filter(m => {
      const ids = [m.id, m._id].filter(Boolean).map(String);
      return !ids.includes(String(messageId));
    }));
  }, []);

  const addOptimisticMessage = useCallback((message: ThreadMessage) => {
    if (getThreadIdentity(selectedThreadRef.current) === String(message.thread_id)) {
      setMessages(prev => [...prev, message]);
    }
  }, []);

  // Update message status (for offline queue sync)
  const updateMessageStatus = useCallback((tempId: string, updates: Partial<ThreadMessage>) => {
    const normalizedTempId = String(tempId);

    setMessages(prev => prev.map(msg => {
      const candidateIds = [
        msg.offline_id,
        msg._offlineId,
        msg.temp_id,
        msg.id,
        msg._id
      ].filter(Boolean).map(value => String(value));

      return candidateIds.includes(normalizedTempId)
        ? { ...msg, ...updates }
        : msg;
    }));
  }, []);

  // Remove optimistic message (if send failed permanently)
  const removeOptimisticMessage = useCallback((tempId: string) => {
    setMessages(prev => prev.filter(msg => msg.offline_id !== tempId));
  }, []);

  // Cleanup typing timeouts on unmount
  useEffect(() => {
    const typingTimeouts = typingTimeoutRef.current;
    return () => {
      typingTimeouts.forEach(timeout => clearTimeout(timeout));
      typingTimeouts.clear();
    };
  }, []);

  return {
    // State
    threads,
    selectedThread,
    messages,
    eligibleMembers,
    loading,
    isCrossClientMode,
    totalClientsInThreads,

    // Search state
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching,
    sortBy,
    setSortBy,
    sortedThreads,

    // Computed values
    totalUnreadCount,
    currentTypingUsers,
    pinnedMentions,

    // Thread operations
    selectThread,
    setThreads,
    setSelectedThread,
    setMessages,
    setEligibleMembers,
    setLoading,

    // Handlers for WebSocket events
    handleThreadsList,
    handleThreadCreated,
    handleThreadUpdated,
    handleThreadDeleted,
    handleSearchResults,
    handleThreadMessages,
    handleNewMessage,
    handleMessageSent,
    handleMessageDeleted,
    handleReactionUpdated,
    handleReadReceipt,
    handleBadgeCountsUpdated,
    handleEligibleMembers,
    handleMemberAdded,
    handleMemberRemoved,
    handleRemovedFromThread,
    handleUserTyping,
    handleUserStoppedTyping,

    // Mention operations
    dismissMention,
    dismissAllMentions,

    // HTTP fallbacks
    fetchThreadsHTTP,
    fetchMessagesHTTP,

    // Optimistic updates for offline queue
    addOptimisticMessage,
    updateMessageStatus,
    removeOptimisticMessage
  };
}

export default useThreads;
